import { initAnalytics } from './analytics/analytics';
import { initAds, oldInitAds } from './ads/ads';
import { initRealUserMonitoring } from './realUserMonitoring/realUserMonitoring';
import {
  getAnalyticsConsent,
  getGdprApplies,
  readPublisherConsentCookie,
  removeNullParams,
  updateGdprApplies,
} from './init.utils';
import { getPageURL } from './pageURL/pageURL';
import { markPerformance } from './performance/index';
import { ErrorReporter } from './errorReporter.type';
import { AdParams, AdSlots } from './ads/ads.types';
import { AnalyticsParams } from './analytics/analytics.types';
import { registerInitAdsForGooglePersonalizationData } from './ads/registerInitAdsForGooglePersonalizationData';

export interface StateAdParams {
  language: string;
  category: string | null;
  pageType: string;
  articleId?: string;
  articleTemplate?: Array<string>;
  channels?: Array<string>;
  contentTags?: Array<string>;
  adSlots: AdSlots;
  headerBidderPublisherID: string;
  mainMedia?: string;
  commercialTags?: Array<string>;
  dfpCategory?: string;
  dfpProperty?: string;
}

export interface StateAnalyticsParams {
  category: string | null;
  articleAuthor?: string;
  articleTitle?: string;
  articlePublicationTime?: string;
  property?: string;
  countryCode: string;
  articleContentCount?: { [key: string]: number };
  articleWordCount?: number;
}

export interface ClientParams {
  platform: string;
  trafficSource: string;
  trafficSourceAndId: string;
  trafficId: string;
  mmUserIdentifier: string;
}

export type CoreParams = StateAdParams & StateAnalyticsParams;

const pageURL = getPageURL();
const monitoringEndpoint = 'https://rum.minutemediaservices.com/';

export const createAdParameters = ({
  language,
  category,
  pageType,
  articleId,
  articleTemplate,
  channels,
  contentTags,
  mainMedia,
  commercialTags,
  dfpProperty,
  platform,
  trafficSource,
  trafficSourceAndId,
  mmUserIdentifier,
}: StateAdParams & ClientParams) => removeNullParams({
  language,
  platform,
  category,
  contentTags,
  pageType,
  test: pageURL.test,
  affiliateId: pageURL.affiliateId,
  articleId,
  articleTemplate,
  distributionChannels: channels,
  mmUserIdentifier,
  trafficSource,
  trafficSourceAndId,
  mainMedia,
  commercialTags,
  dfpProperty,
});

export const createAnalyticsParametersWithAdParams = (adParameters: AdParams, analyticsParams: AnalyticsParams) => ({
  ...adParameters,
  ...analyticsParams,
});

export const createAnalyticsParams = ({
  category,
  articleAuthor,
  articleTitle,
  articlePublicationTime,
  property,
  countryCode,
  articleContentCount,
  articleWordCount,
  trafficId,
}: StateAnalyticsParams & ClientParams) => ({
  pageIdentifier: category,
  geoDetected: countryCode,
  analyticsConsent: getAnalyticsConsent(readPublisherConsentCookie()),
  markupFormat: 'web',
  gdprApplies: getGdprApplies(),
  trafficReferrer: document.referrer,
  trafficId,
  articleAuthor,
  articleTitle,
  articlePublicationTime,
  property,
  articleContentCount,
  articleWordCount,
});

const iniAdsCallback = (adSlots: AdSlots, params: AdParams, adsCategory: string, headerBidderPublisherID: string, errorProvider: ErrorReporter, shouldNotPersonalizeAds: boolean) => {
  try {
    oldInitAds(adSlots, params, adsCategory, headerBidderPublisherID, errorProvider, shouldNotPersonalizeAds);
    markPerformance('init-ads');
  } catch (e) {
    errorProvider.reportException(e);
  }
};

export function init(params: CoreParams & ClientParams, errorProvider: ErrorReporter) {
  const { pageType, adSlots, headerBidderPublisherID, countryCode, category, dfpCategory } = params;
  const adParameters = createAdParameters(params);
  const adsCategory = dfpCategory || category;

  initRealUserMonitoring(countryCode, pageType, monitoringEndpoint, errorProvider);

  try {
    initAnalytics(createAnalyticsParametersWithAdParams(adParameters, createAnalyticsParams(params)));
    markPerformance('init-analytics');
  } catch (e) {
    errorProvider.reportException(e);
  }
  try {
    if (adSlots) {
      if (!window.__tcfapi) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        registerInitAdsForGooglePersonalizationData(adSlots, adParameters, adsCategory!, headerBidderPublisherID, errorProvider, iniAdsCallback);
      } else {
        let cmpuishown = false;
        let useractioncomplete = false;
        window.__tcfapi('addEventListener', 2, (tcData: any, success: boolean) => {
          if (success) {
            if (tcData.eventStatus === 'tcloaded') {
              initAds(adSlots, adParameters, adsCategory!, headerBidderPublisherID, errorProvider);
              markPerformance('init-ads');
            } else if (tcData.eventStatus === 'cmpuishown') {
              cmpuishown = true;
              if (useractioncomplete) {
                initAds(adSlots, adParameters, adsCategory!, headerBidderPublisherID, errorProvider);
                markPerformance('init-ads');
              }
            } else if (tcData.eventStatus === 'useractioncomplete') {
              useractioncomplete = true;
              if (cmpuishown) {
                initAds(adSlots, adParameters, adsCategory!, headerBidderPublisherID, errorProvider);
                markPerformance('init-ads');
              }
            }
          } else {
            console.error('"addEventListener" event CB is unsuccessful, therefore initAds() was not invoked');
          }
        });
      }
    }
  } catch (e) {
    errorProvider.reportException(e);
  }
  try {
    updateGdprApplies();
  } catch (e) {
    errorProvider.reportException(e);
  }
}
